// v2/config/feature-flags.config.ts — Feature flags and status display config
// Extracted from v1: features/applications/config.ts (getStatusColor, APPLICATION_STATUS)

import { ApplicationState } from '../shared/types/domain';
import { ProductType } from '../shared/types/domain';
import { FeatureFlagConfig, StatusDisplayConfig } from './types';

// ─── AMS-Authoritative Residence Verification ────────────────────────────────
// Products that use the AMS-authoritative residence verification flow.

export const AMS_AUTHORITATIVE_RESIDENCE_VERIFICATION_PRODUCTS: Set<ProductType> = new Set([
  ProductType.PERSONAL_LOAN,
]);

/**
 * Returns true if the given product type uses the AMS-authoritative
 * residence verification flow instead of the legacy flow.
 */
export function usesAmsAuthoritativeResidenceVerification(
  productType: ProductType | string | null | undefined,
): boolean {
  if (!productType) return false;
  // Accept raw strings by mapping them to the enum value
  const mapped = Object.values(ProductType).find((v) => v === productType);
  return mapped ? AMS_AUTHORITATIVE_RESIDENCE_VERIFICATION_PRODUCTS.has(mapped) : false;
}

// ─── Feature Flags ───────────────────────────────────────────────────────────
// All flags default to false (v1 pages served). Set to true to redirect to v2.

export const FEATURE_FLAGS: FeatureFlagConfig = {
  dashboard: false,
  leads: false,
  applications: false,
  tasks: false,
  groups: false,
  loginHistory: false,
  settings: false,
  residenceVerification: false,
  emiCalculator: false,
};

// ─── Status Display Configuration ────────────────────────────────────────────
// Maps each ApplicationState to a human-readable label and Tailwind color classes

export const STATUS_DISPLAY_CONFIG: Record<ApplicationState, StatusDisplayConfig> = {
  [ApplicationState.DRAFT]: {
    label: 'Draft',
    colorClass: 'bg-gray-200 text-gray-800',
  },
  [ApplicationState.SUBMITTED]: {
    label: 'Submitted',
    colorClass: 'bg-blue-200 text-blue-900',
  },
  [ApplicationState.SOFT_APPROVED]: {
    label: 'Soft Approved',
    colorClass: 'bg-purple-200 text-purple-900',
  },
  [ApplicationState.APPROVED]: {
    label: 'Approved',
    colorClass: 'bg-green-200 text-green-900',
  },
  [ApplicationState.PENDING_VERIFICATION]: {
    label: 'Pending Verification',
    colorClass: 'bg-orange-200 text-orange-900',
  },
  [ApplicationState.VERIFICATION_COMPLETE]: {
    label: 'Verification Complete',
    colorClass: 'bg-cyan-200 text-cyan-900',
  },
  [ApplicationState.INVOICE_COLLECTED]: {
    label: 'Invoice Collected',
    colorClass: 'bg-yellow-200 text-yellow-900',
  },
  [ApplicationState.MANDATED]: {
    label: 'Mandated',
    colorClass: 'bg-teal-200 text-teal-900',
  },
  [ApplicationState.SIGNED]: {
    label: 'Signed',
    colorClass: 'bg-indigo-200 text-indigo-900',
  },
  [ApplicationState.DISBURSED]: {
    label: 'Disbursed',
    colorClass: 'bg-emerald-200 text-emerald-900',
  },
  [ApplicationState.REJECTED]: {
    label: 'Rejected',
    colorClass: 'bg-red-200 text-red-900',
  },
};
